import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TicketCardRow, TicketGridRow, TicketRow } from './TicketRows';
import { ValueSheet, ValueSheetOption } from './ValueSheet';
import { UndoToast } from './UndoToast';
import { useListFetch } from './useListFetch';
import { ListStateShell } from './ListStateShell';

/** TicketsGrid — #62 next-phase Tickets depth spec (UXeng §2): a TABLE (Ticket ID/Subject/Status/
 *  Priority/Assignee), same responsive tier-switch as Invoices/Pipeline/Accounts. */
export const TicketsGrid: React.FC<{
  selectedId?: string | null;
  onOpenTicket: (ticket: TicketRow) => void;
  refreshKey?: number;
}> = ({ selectedId, onOpenTicket, refreshKey }) => {
  const { t } = useTranslation('common');

  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' && window.innerWidth >= 1024);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // X-1 via useListFetch (P38 surface 7 of 10) — a failed ticket list must never read as
  // "nothing is wrong"; the empty copy renders only on a trustworthy empty list.
  const list = useListFetch<TicketRow[]>('/api/tickets', { refreshKey });
  const tickets = list.data ?? [];

  // DN-7 (ruled: WIRE, don't demote) — the list-level assignee edit, running the reference loop
  // (LeadsGrid.applyEdit): optimistic → PUT → undo toast with a working undo → revert + error on
  // failure. Options are the account's members (a BOUNDED set — the plain sheet is the right
  // control; the searchable variant is for tenant-wide sets).
  const assignees = useListFetch<{ id: string; name: string | null }[]>('/api/tasks/assignees');
  const [editing, setEditing] = useState<{ ticket: TicketRow; anchor: HTMLElement; shape: 'sheet' | 'popover' } | null>(null);
  const [toast, setToast] = useState<{ message: string; variant: 'undo' | 'error'; onUndo?: () => void } | null>(null);
  const memberOptions: ValueSheetOption[] = (assignees.data ?? []).map((m) => ({ value: m.id, label: m.name ?? '—' }));

  const applyAssignee = async (ticket: TicketRow, assigneeId: string | null) => {
    const prevId = ticket.assigneeId; const prevName = ticket.assigneeName;
    const nextName = assigneeId ? ((assignees.data ?? []).find((m) => m.id === assigneeId)?.name ?? null) : null;
    list.setData((ts) => (ts ?? []).map((x) => x.id === ticket.id ? { ...x, assigneeId, assigneeName: nextName } : x)); // optimistic
    setEditing(null);
    try {
      const res = await fetch(`/api/tickets/${ticket.id}/assignee`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ assigneeId }) });
      if (!res.ok) throw new Error(String(res.status));
      setToast({ message: t('ticketsGrid.toast.assigneeUpdated'), variant: 'undo', onUndo: () => { setToast(null); void applyAssignee({ ...ticket, assigneeId, assigneeName: nextName }, prevId); } });
    } catch {
      list.setData((ts) => (ts ?? []).map((x) => x.id === ticket.id ? { ...x, assigneeId: prevId, assigneeName: prevName } : x)); // revert
      setToast({ message: t('ticketsGrid.toast.assigneeFailed'), variant: 'error' });
    }
  };
  const onEditAssignee = (ticket: TicketRow, anchor: HTMLElement, shape: 'sheet' | 'popover') => setEditing({ ticket, anchor, shape });

  return (
    <div className="relative w-full">
      <ListStateShell status={list.status} testIdBase="tickets-grid" onRetry={list.retry}>
      {!isDesktop ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-2">
          {tickets.map((tk) => <TicketCardRow key={tk.id} ticket={tk} selected={tk.id === selectedId} onOpenTicket={onOpenTicket} onEditAssignee={onEditAssignee} />)}
          {tickets.length === 0 && <div className="col-span-full text-center text-sm text-text-muted py-10">{t('ticketsGrid.empty')}</div>}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-start border-collapse">
            <thead>
              <tr className="text-label-caps uppercase text-text-muted border-b border-border-neutral">
                <th className="sticky z-10 bg-surface p-cell-y px-cell-x text-start [inset-inline-start:0]">{t('ticketsGrid.columns.id')}</th>
                <th className="p-cell-y px-cell-x text-start">{t('ticketsGrid.columns.subject')}</th>
                <th className="p-cell-y px-cell-x text-start">{t('ticketsGrid.columns.status')}</th>
                <th className="p-cell-y px-cell-x text-start">{t('ticketsGrid.columns.priority')}</th>
                <th className="p-cell-y px-cell-x text-start">{t('ticketsGrid.columns.assignee')}</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((tk) => <TicketGridRow key={tk.id} ticket={tk} selected={tk.id === selectedId} onOpenTicket={onOpenTicket} onEditAssignee={onEditAssignee} />)}
            </tbody>
          </table>
          {tickets.length === 0 && <div className="text-center text-sm text-text-muted py-10">{t('ticketsGrid.empty')}</div>}
        </div>
      )}
      </ListStateShell>

      {editing && (
        <ValueSheet shape={editing.shape} title={`${t('ticketsGrid.columns.assignee')} — ${editing.ticket.subject}`}
          options={memberOptions} current={editing.ticket.assigneeId} anchor={editing.anchor}
          onSelect={(v) => applyAssignee(editing.ticket, v)} onClose={() => setEditing(null)} />
      )}
      {toast && <UndoToast message={toast.message} variant={toast.variant} onUndo={toast.onUndo} onExpire={() => setToast(null)} />}
    </div>
  );
};
